import { useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

// ────────────────────────────────────────────────────────────────
// Tipos
// ────────────────────────────────────────────────────────────────

export type MentorshipSessionStatus = 'draft' | 'planned' | 'completed' | 'cancelled';
/** lider = mentor líder de la mentoría (uno, igual a lead_mentor_id); apoyo = acompaña al líder en toda la mentoría. */
export type MentorshipMentorRole = 'lider' | 'apoyo';
export type MentorshipTaskStatus = 'pending' | 'in_progress' | 'done' | 'cancelled';

export interface MentorshipProjectType {
  id: string;
  name: string;
  sort_order: number;
  is_active: boolean;
}

export interface MentorshipProject {
  id: string;
  organization_id: string;
  name: string;
  scope_description: string | null;
  type_id: string | null;
  contracted_hours: number;
  start_date: string | null;
  end_date: string | null;
  lead_mentor_id: string | null;
  contract_reference: string | null;
  status: string;
  /** Regla de redondeo de horas de esta mentoría. */
  hours_rounding: MentorshipHoursRounding;
  created_at: string;
  organizations?: { id: string; name: string } | null;
  mentorship_project_types?: { id: string; name: string } | null;
  mentorship_project_mentors?: { id: string; user_id: string; role: MentorshipMentorRole }[];
}

export interface MentorshipGroup {
  id: string;
  project_id: string;
  name: string;
  group_type: string | null;
  objective: string | null;
  diag_current_situation: string | null;
  diag_gap: string | null;
  diag_expected_result: string | null;
  diag_indicator: string | null;
  estimated_hours: number;
  status: string;
  closing_result: string | null;
  closing_technical_approval: string | null;
  closing_recommendation: string | null;
  closed_at: string | null;
  /** Mentor de apoyo del grupo: quien normalmente da sus sesiones. Vacío = lo atiende el mentor líder. */
  support_mentor_id: string | null;
  mentorship_group_participants?: MentorshipGroupParticipant[];
}

export interface MentorshipGroupParticipant {
  id: string;
  group_id: string;
  user_id: string;
  is_leader: boolean;
}

export interface MentorshipSession {
  id: string;
  group_id: string;
  name: string;
  objective: string | null;
  scheduled_date: string | null;
  start_time: string | null;
  duration_minutes: number;
  modality: string;
  meet_link: string | null;
  recording_link: string | null;
  transcript_text: string | null;
  transcript_url: string | null;
  notes: string | null;
  attendees: string[];
  status: MentorshipSessionStatus;
  completed_at: string | null;
}

export interface MentorshipTask {
  id: string;
  group_id: string;
  session_id: string | null;
  title: string;
  description: string | null;
  assignee_id: string | null;
  due_date: string | null;
  status: MentorshipTaskStatus;
  evidence_url: string | null;
  evidence_text: string | null;
  completed_at: string | null;
  created_at: string;
}

export interface MentorshipExtraWork {
  id: string;
  project_id: string;
  group_id: string | null;
  work_date: string;
  hours: number;
  description: string | null;
  created_by: string | null;
  created_at: string;
}

export interface MentorshipExtraWorkRow extends MentorshipExtraWork {
  group_name: string | null;
  author_name: string | null;
  /** Atribución vigente (ver fetchCurrentAttribution). */
  mentor_ids: string[];
  mentor_names: string[];
}


export interface MentorshipHoursLogMentor {
  mentor_id: string;
  full_name: string | null;
  is_primary: boolean;
}

export interface MentorshipHoursLogEntry {
  id: string;
  project_id: string;
  group_id: string | null;
  session_id: string | null;
  event_type: string;
  hours_delta: number | null;
  description: string | null;
  actor_id: string | null;
  created_at: string;
  /** Mentores atribuidos a este registro (no multiplican las horas, solo reporte). */
  mentors: MentorshipHoursLogMentor[];
}

export interface MentorshipPerson {
  id: string;
  full_name: string;
  email: string;
  organization_id: string | null;
  company: string | null;
}

/**
 * Atribuye mentores a un registro de mentorship_hours_log ya insertado. No
 * afecta hours_delta: las horas nunca se multiplican por mentor, esto es solo
 * atribución/reporte (ver mentorship_hours_log_mentors en 0007_mentorship_mentors.sql).
 */
async function attachHoursLogMentors(hoursLogId: string, mentorIds: string[] | undefined) {
  const ids = Array.from(new Set((mentorIds ?? []).filter(Boolean)));
  if (!ids.length) return;
  const rows = ids.map((mentor_id) => ({ hours_log_id: hoursLogId, mentor_id }));
  const { error } = await supabase.from('mentorship_hours_log_mentors').insert(rows);
  if (error) throw error;
}

/**
 * Atribución vigente de cada registro (sesión o trabajo fuera de sesión): los
 * mentores del asiento más reciente de ese registro que tenga mentores. No
 * filtra por event_type, así cubre también los asientos cargados a mano.
 * Devuelve Map<registroId, mentorIds>; los registros sin atribución no aparecen.
 */
async function fetchCurrentAttribution(
  column: 'session_id' | 'extra_work_id',
  ids: string[]
): Promise<Map<string, string[]>> {
  const result = new Map<string, string[]>();
  if (!ids.length) return result;
  const { data: logs, error } = await supabase
    .from('mentorship_hours_log')
    .select('id, session_id, extra_work_id, created_at')
    .in(column, ids)
    .order('created_at', { ascending: false });
  if (error) throw error;
  if (!logs?.length) return result;

  const { data: links, error: linksError } = await supabase
    .from('mentorship_hours_log_mentors')
    .select('hours_log_id, mentor_id')
    .in('hours_log_id', logs.map((l) => l.id));
  if (linksError) throw linksError;
  const mentorsByLog = new Map<string, string[]>();
  (links ?? []).forEach((l) => mentorsByLog.set(l.hours_log_id, [...(mentorsByLog.get(l.hours_log_id) ?? []), l.mentor_id]));

  for (const log of logs) {
    const registroId = log[column];
    if (!registroId || result.has(registroId)) continue;
    const mentors = mentorsByLog.get(log.id);
    if (mentors?.length) result.set(registroId, mentors);
  }
  return result;
}

const sameMentorSet = (a: string[], b: string[]) =>
  a.length === b.length && a.every((id) => b.includes(id));

/** IDs de los mentores de una mentoría: mentor líder + mentores de apoyo, sin duplicados. */
export function projectMentorIds(
  project: Pick<MentorshipProject, 'lead_mentor_id' | 'mentorship_project_mentors'> | null | undefined
): string[] {
  if (!project) return [];
  const ids = new Set<string>();
  if (project.lead_mentor_id) ids.add(project.lead_mentor_id);
  (project.mentorship_project_mentors ?? []).forEach((m) => ids.add(m.user_id));
  return Array.from(ids);
}

/** IDs de los mentores de apoyo de la mentoría (rol apoyo, nunca el líder). */
export function projectSupportMentorIds(
  project: Pick<MentorshipProject, 'lead_mentor_id' | 'mentorship_project_mentors'> | null | undefined
): string[] {
  return (project?.mentorship_project_mentors ?? [])
    .filter((m) => m.role === 'apoyo' && m.user_id !== project?.lead_mentor_id)
    .map((m) => m.user_id);
}

export type MentorshipHoursRounding = 'quarter_nearest' | 'half_up';

/** Texto para explicar la regla de redondeo de una mentoría. */
export const ROUNDING_LABEL: Record<MentorshipHoursRounding, string> = {
  quarter_nearest: 'cuarto de hora más cercano',
  half_up: 'media hora hacia arriba',
};

/**
 * Horas que descuenta de la bolsa una sesión, según la regla de su mentoría.
 * Misma fórmula que mentorship_round_session_hours() en la base.
 * quarter_nearest: 12 min → 0,25 h; 123 min → 2 h; 128 min → 2,25 h. half_up: 71 min → 1,5 h.
 */
export const billedSessionHours = (minutes: number | null | undefined, rounding: MentorshipHoursRounding = 'quarter_nearest') => {
  const m = Number(minutes || 0);
  return rounding === 'half_up' ? Math.ceil(m / 30) * 0.5 : Math.floor((m + 7.5) / 15) * 0.25;
};

/** Trabajo fuera de sesión, como mentorship_round_extra_hours() en la base. */
export const billedExtraHours = (hours: number | null | undefined, rounding: MentorshipHoursRounding = 'quarter_nearest') => {
  const h = Number(hours || 0);
  return rounding === 'half_up' ? Math.ceil(h * 2) / 2 : Math.floor(h * 4 + 0.5) / 4;
};

/** Regla de redondeo vigente de una mentoría (para mutaciones y reportes). */
async function fetchProjectRounding(projectId: string): Promise<MentorshipHoursRounding> {
  const { data } = await supabase.from('mentorship_projects').select('hours_rounding').eq('id', projectId).maybeSingle();
  return ((data as any)?.hours_rounding as MentorshipHoursRounding) ?? 'quarter_nearest';
}

export const SESSION_STATUS_LABEL: Record<MentorshipSessionStatus, string> = {
  draft: 'Borrador',
  planned: 'Planificada',
  completed: 'Realizada',
  cancelled: 'Cancelada',
};

export const TASK_STATUS_LABEL: Record<MentorshipTaskStatus, string> = {
  pending: 'Pendiente',
  in_progress: 'En progreso',
  done: 'Completada',
  cancelled: 'Cancelada',
};

// ────────────────────────────────────────────────────────────────
// Catálogo de tipos
// ────────────────────────────────────────────────────────────────

export function useMentorshipTypes() {
  return useQuery({
    queryKey: ['mentorship-types'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('mentorship_project_types')
        .select('*')
        .order('sort_order');
      if (error) throw error;
      return (data ?? []) as MentorshipProjectType[];
    },
  });
}

export function useSaveMentorshipType() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id?: string; name: string; sort_order?: number; is_active?: boolean }) => {
      if (input.id) {
        const { error } = await supabase
          .from('mentorship_project_types')
          .update({ name: input.name, sort_order: input.sort_order ?? 0, is_active: input.is_active ?? true })
          .eq('id', input.id);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('mentorship_project_types')
          .insert({ name: input.name, sort_order: input.sort_order ?? 0 });
        if (error) throw error;
      }
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['mentorship-types'] }),
  });
}

export function useDeleteMentorshipType() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('mentorship_project_types').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['mentorship-types'] }),
  });
}

// ────────────────────────────────────────────────────────────────
// Proyectos
// ────────────────────────────────────────────────────────────────

const PROJECT_SELECT =
  '*, organizations(id, name), mentorship_project_types(id, name), mentorship_project_mentors(id, user_id, role)';

export function useMentorshipProjects() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ['mentorship-projects'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('mentorship_projects')
        .select(PROJECT_SELECT)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as unknown as MentorshipProject[];
    },
    enabled: !!user,
  });
}

export function useMentorshipProject(projectId?: string) {
  return useQuery({
    queryKey: ['mentorship-project', projectId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('mentorship_projects')
        .select(PROJECT_SELECT)
        .eq('id', projectId!)
        .maybeSingle();
      if (error) throw error;
      return (data ?? null) as unknown as MentorshipProject | null;
    },
    enabled: !!projectId,
  });
}

export function useSaveMentorshipProject() {
  const qc = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async (input: Partial<MentorshipProject> & { id?: string; supportMentorIds?: string[] }) => {
      const { id, supportMentorIds, organizations, mentorship_project_types, mentorship_project_mentors, ...rest } =
        input as any;
      const payload = {
        organization_id: rest.organization_id,
        name: rest.name,
        scope_description: rest.scope_description ?? null,
        type_id: rest.type_id || null,
        contracted_hours: Number(rest.contracted_hours ?? 0),
        start_date: rest.start_date || null,
        end_date: rest.end_date || null,
        lead_mentor_id: rest.lead_mentor_id || null,
        contract_reference: rest.contract_reference ?? null,
        status: rest.status ?? 'active',
      };

      let projectId = id as string | undefined;
      let previousHours: number | null = null;

      if (projectId) {
        const { data: before } = await supabase
          .from('mentorship_projects')
          .select('contracted_hours')
          .eq('id', projectId)
          .maybeSingle();
        previousHours = before ? Number(before.contracted_hours) : null;
        const { error } = await supabase
          .from('mentorship_projects')
          .update({ ...payload, updated_at: new Date().toISOString() })
          .eq('id', projectId);
        if (error) throw error;
      } else {
        const { data, error } = await supabase
          .from('mentorship_projects')
          .insert({ ...payload, created_by: user?.id ?? null })
          .select('id')
          .single();
        if (error) throw error;
        projectId = data.id;
      }

      if (supportMentorIds) {
        // El mentor líder lo mantiene en la tabla (con rol lider) un trigger sobre
        // lead_mentor_id; aquí solo se sincronizan los de apoyo, sin tocar al líder.
        const supports = Array.from(
          new Set((supportMentorIds as string[]).filter((uid) => uid && uid !== payload.lead_mentor_id))
        );
        if (supports.length) {
          const { error } = await supabase
            .from('mentorship_project_mentors')
            .upsert(
              supports.map((uid) => ({ project_id: projectId!, user_id: uid, role: 'apoyo' })),
              { onConflict: 'project_id,user_id' }
            );
          if (error) throw error;
        }
        const keep = [payload.lead_mentor_id, ...supports].filter(Boolean) as string[];
        let removeQuery = supabase.from('mentorship_project_mentors').delete().eq('project_id', projectId!);
        if (keep.length) removeQuery = removeQuery.not('user_id', 'in', `(${keep.join(',')})`);
        const { error: removeError } = await removeQuery;
        if (removeError) throw removeError;

        // Si quien apoyaba un grupo sale de la mentoría, el grupo queda sin apoyo
        // (lo atiende el mentor líder), visible en el listado.
        let clear = supabase
          .from('mentorship_groups')
          .update({ support_mentor_id: null })
          .eq('project_id', projectId!)
          .not('support_mentor_id', 'is', null);
        if (keep.length) clear = clear.not('support_mentor_id', 'in', `(${keep.join(',')})`);
        const { error: clearError } = await clear;
        if (clearError) throw clearError;
      }

      if (previousHours !== null && previousHours !== payload.contracted_hours) {
        await supabase.from('mentorship_hours_log').insert({
          project_id: projectId!,
          event_type: 'contracted_hours_changed',
          hours_delta: payload.contracted_hours - previousHours,
          description: `Horas contratadas: ${previousHours} → ${payload.contracted_hours}`,
        });
      }

      return projectId!;
    },
    onSuccess: (projectId) => {
      qc.invalidateQueries({ queryKey: ['mentorship-projects'] });
      qc.invalidateQueries({ queryKey: ['mentorship-project', projectId] });
      qc.invalidateQueries({ queryKey: ['mentorship-hours-log', projectId] });
      qc.invalidateQueries({ queryKey: ['mentorship-groups', projectId] });
      qc.invalidateQueries({ queryKey: ['mentorship-group'] });
    },
  });
}

export function useCloseMentorshipProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ projectId, status }: { projectId: string; status: 'active' | 'closed' }) => {
      const { error } = await supabase
        .from('mentorship_projects')
        .update({ status, updated_at: new Date().toISOString() })
        .eq('id', projectId);
      if (error) throw error;
      await supabase.from('mentorship_hours_log').insert({
        project_id: projectId,
        event_type: status === 'closed' ? 'project_closed' : 'project_reopened',
        description: status === 'closed' ? 'Proyecto cerrado' : 'Proyecto reabierto',
      });
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['mentorship-projects'] });
      qc.invalidateQueries({ queryKey: ['mentorship-project', vars.projectId] });
    },
  });
}

// ────────────────────────────────────────────────────────────────
// Grupos
// ────────────────────────────────────────────────────────────────

export function useMentorshipGroups(projectId?: string) {
  return useQuery({
    queryKey: ['mentorship-groups', projectId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('mentorship_groups')
        .select('*, mentorship_group_participants(*)')
        .eq('project_id', projectId!)
        .order('created_at');
      if (error) throw error;
      return (data ?? []) as unknown as MentorshipGroup[];
    },
    enabled: !!projectId,
  });
}

export function useMentorshipGroup(groupId?: string) {
  return useQuery({
    queryKey: ['mentorship-group', groupId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('mentorship_groups')
        .select('*, mentorship_group_participants(*)')
        .eq('id', groupId!)
        .maybeSingle();
      if (error) throw error;
      return (data ?? null) as unknown as MentorshipGroup | null;
    },
    enabled: !!groupId,
  });
}

export function useSaveMentorshipGroup() {
  const qc = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async (
      input: Partial<MentorshipGroup> & {
        id?: string;
        project_id: string;
        participants?: { user_id: string; is_leader: boolean }[];
      }
    ) => {
      const { id, participants, mentorship_group_participants, ...rest } = input as any;
      const payload = {
        project_id: rest.project_id,
        name: rest.name,
        group_type: rest.group_type ?? null,
        objective: rest.objective ?? null,
        diag_current_situation: rest.diag_current_situation ?? null,
        diag_gap: rest.diag_gap ?? null,
        diag_expected_result: rest.diag_expected_result ?? null,
        diag_indicator: rest.diag_indicator ?? null,
        estimated_hours: Number(rest.estimated_hours ?? 0),
        support_mentor_id: rest.support_mentor_id || null,
      };

      let groupId = id as string | undefined;
      if (groupId) {
        const { error } = await supabase
          .from('mentorship_groups')
          .update({ ...payload, updated_at: new Date().toISOString() })
          .eq('id', groupId);
        if (error) throw error;
      } else {
        const { data, error } = await supabase
          .from('mentorship_groups')
          .insert({ ...payload, created_by: user?.id ?? null })
          .select('id')
          .single();
        if (error) throw error;
        groupId = data.id;
      }

      if (participants) {
        await supabase.from('mentorship_group_participants').delete().eq('group_id', groupId!);
        if (participants.length) {
          const { error } = await supabase.from('mentorship_group_participants').insert(
            participants.map((p) => ({ group_id: groupId!, user_id: p.user_id, is_leader: p.is_leader }))
          );
          if (error) throw error;
        }
      }
      return groupId!;
    },
    onSuccess: (_groupId, vars) => {
      qc.invalidateQueries({ queryKey: ['mentorship-groups', vars.project_id] });
      qc.invalidateQueries({ queryKey: ['mentorship-group'] });
    },
  });
}

export function useCloseMentorshipGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      groupId: string;
      projectId: string;
      closing_result: string;
      closing_technical_approval: string;
      closing_recommendation: string;
    }) => {
      const { error } = await supabase
        .from('mentorship_groups')
        .update({
          status: 'closed',
          closing_result: input.closing_result,
          closing_technical_approval: input.closing_technical_approval,
          closing_recommendation: input.closing_recommendation,
          closed_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        })
        .eq('id', input.groupId);
      if (error) throw error;
      await supabase.from('mentorship_hours_log').insert({
        project_id: input.projectId,
        group_id: input.groupId,
        event_type: 'group_closed',
        description: 'Grupo cerrado',
      });
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['mentorship-groups', vars.projectId] });
      qc.invalidateQueries({ queryKey: ['mentorship-group', vars.groupId] });
      qc.invalidateQueries({ queryKey: ['mentorship-hours-log', vars.projectId] });
    },
  });
}

export function useDeleteMentorshipGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ groupId, projectId }: { groupId: string; projectId: string }) => {
      const [{ data: group }, { data: sessions }] = await Promise.all([
        supabase.from('mentorship_groups').select('name').eq('id', groupId).maybeSingle(),
        supabase.from('mentorship_sessions').select('id, duration_minutes, status').eq('group_id', groupId),
      ]);
      const completedSessions = (sessions ?? []).filter((s) => s.status === 'completed');
      const rounding = await fetchProjectRounding(projectId);
      const executedHours = completedSessions.reduce((sum, s) => sum + billedSessionHours(s.duration_minutes, rounding), 0);

      // Unión de la atribución vigente de las sesiones realizadas del grupo, para
      // que la reversa del grupo completo quede a nombre de todos esos mentores.
      const attribution = await fetchCurrentAttribution('session_id', completedSessions.map((s) => s.id));
      const carriedMentorIds = Array.from(new Set(Array.from(attribution.values()).flat()));

      // Borrar el grupo hace CASCADE sobre sus sesiones (FK del grupo), así que
      // sin este asiento las horas de las sesiones 'completed' del grupo
      // quedarían huérfanas en la bitácora igual que en useDeleteMentorshipSession.
      const { error } = await supabase.from('mentorship_groups').delete().eq('id', groupId);
      if (error) throw error;

      const { data: logRow } = await supabase.from('mentorship_hours_log').insert({
        project_id: projectId,
        group_id: null,
        event_type: 'group_deleted',
        hours_delta: executedHours > 0 ? -executedHours : null,
        description: `Grupo eliminado: "${group?.name ?? groupId}"`,
      }).select('id').single();
      if (logRow) await attachHoursLogMentors(logRow.id, carriedMentorIds);
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['mentorship-groups', vars.projectId] });
      qc.invalidateQueries({ queryKey: ['mentorship-hours', vars.projectId] });
      qc.invalidateQueries({ queryKey: ['mentorship-hours-log', vars.projectId] });
      qc.invalidateQueries({ queryKey: ['mentorship-mentor-report', vars.projectId] });
    },
  });
}

// ────────────────────────────────────────────────────────────────
// Sesiones
// ────────────────────────────────────────────────────────────────

export function useMentorshipSessions(groupId?: string) {
  return useQuery({
    queryKey: ['mentorship-sessions', groupId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('mentorship_sessions')
        .select('*')
        .eq('group_id', groupId!)
        .order('scheduled_date', { ascending: true, nullsFirst: false });
      if (error) throw error;
      return (data ?? []) as unknown as MentorshipSession[];
    },
    enabled: !!groupId,
  });
}

export function useProjectSessions(projectId?: string) {
  return useQuery({
    queryKey: ['mentorship-project-sessions', projectId],
    queryFn: async () => {
      const { data: groups, error: gErr } = await supabase
        .from('mentorship_groups')
        .select('id, name')
        .eq('project_id', projectId!);
      if (gErr) throw gErr;
      const ids = (groups ?? []).map((g) => g.id);
      if (!ids.length) return [] as (MentorshipSession & { groupName: string })[];
      const { data, error } = await supabase
        .from('mentorship_sessions')
        .select('*')
        .in('group_id', ids)
        .order('scheduled_date', { ascending: true, nullsFirst: false });
      if (error) throw error;
      const nameById = new Map((groups ?? []).map((g) => [g.id, g.name]));
      return ((data ?? []) as unknown as MentorshipSession[]).map((s) => ({
        ...s,
        groupName: nameById.get(s.group_id) ?? '',
      }));
    },
    enabled: !!projectId,
  });
}

export function useSaveMentorshipSession() {
  const qc = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async (
      input: Partial<MentorshipSession> & {
        id?: string; group_id: string; projectId: string;
        /** Mentores que dieron la sesión (atribución; no multiplica horas). */
        mentorIds?: string[];
      }
    ) => {
      const { id, projectId, mentorIds, ...rest } = input as any;
      const payload = {
        group_id: rest.group_id,
        name: rest.name,
        objective: rest.objective ?? null,
        scheduled_date: rest.scheduled_date || null,
        start_time: rest.start_time || null,
        duration_minutes: Number(rest.duration_minutes ?? 60),
        modality: rest.modality ?? 'virtual',
        meet_link: rest.meet_link ?? null,
        recording_link: rest.recording_link ?? null,
        transcript_text: rest.transcript_text ?? null,
        notes: rest.notes ?? null,
        attendees: rest.attendees ?? [],
        status: rest.status ?? 'draft',
        completed_at: rest.status === 'completed' ? new Date().toISOString() : null,
      };
      const statusLabel = SESSION_STATUS_LABEL[payload.status as MentorshipSessionStatus] ?? payload.status;

      // hours_delta refleja el cambio en lo consumido de la bolsa: horas cobradas
      // (billedSessionHours) de las sesiones 'completed'.
      const rounding = await fetchProjectRounding(projectId);
      const logSessionEvent = async (sessionId: string, event_type: string, hours_delta: number | null, description: string) => {
        const { data: logRow } = await supabase.from('mentorship_hours_log').insert({
          project_id: projectId,
          group_id: payload.group_id,
          session_id: sessionId,
          event_type,
          hours_delta,
          description,
        }).select('id').single();
        if (logRow) await attachHoursLogMentors(logRow.id, mentorIds);
      };

      if (id) {
        const [{ data: before }, attribution] = await Promise.all([
          supabase.from('mentorship_sessions').select('duration_minutes, status').eq('id', id).maybeSingle(),
          fetchCurrentAttribution('session_id', [id]),
        ]);
        const { error } = await supabase
          .from('mentorship_sessions')
          .update({ ...payload, updated_at: new Date().toISOString() })
          .eq('id', id);
        if (error) throw error;
        if (!before) return id as string;

        const wasCompleted = before.status === 'completed';
        const isCompleted = payload.status === 'completed';
        const oldHours = billedSessionHours(before.duration_minutes, rounding);
        const newHours = billedSessionHours(payload.duration_minutes, rounding);
        let logged = false;

        if (before.duration_minutes !== payload.duration_minutes) {
          // Solo mueve la bolsa si la sesión estaba y sigue realizada; si además
          // cambió de estado, el asiento de estado lleva la duración completa.
          await logSessionEvent(
            id,
            'session_duration_changed',
            wasCompleted && isCompleted ? newHours - oldHours : null,
            `Duración de "${payload.name}": ${before.duration_minutes} → ${payload.duration_minutes} min`,
          );
          logged = true;
        }
        if (before.status !== payload.status) {
          const delta = !wasCompleted && isCompleted ? newHours : wasCompleted && !isCompleted ? -oldHours : null;
          const what = isCompleted
            ? 'marcada como realizada'
            : wasCompleted ? `deja de estar realizada (${statusLabel})` : statusLabel;
          await logSessionEvent(id, `session_${payload.status}`, delta, `Sesión "${payload.name}": ${what}`);
          logged = true;
        }
        // Corregir solo los mentores de una sesión ya realizada también queda en la
        // bitácora (sin horas): la atribución vigente es la del asiento más reciente.
        const currentMentors = attribution.get(id) ?? [];
        if (!logged && isCompleted && mentorIds?.length && !sameMentorSet(mentorIds, currentMentors)) {
          await logSessionEvent(id, 'session_mentors_changed', null, `Mentores de "${payload.name}" actualizados`);
        }
        return id as string;
      }

      const { data, error } = await supabase
        .from('mentorship_sessions')
        .insert({ ...payload, created_by: user?.id ?? null })
        .select('id')
        .single();
      if (error) throw error;
      // Una sesión creada ya realizada (o planificada) deja su asiento igual que un
      // cambio de estado; sin esto sus horas se consumían sin rastro ni mentores.
      if (payload.status !== 'draft') {
        await logSessionEvent(
          data.id,
          `session_${payload.status}`,
          payload.status === 'completed' ? billedSessionHours(payload.duration_minutes, rounding) : null,
          `Sesión "${payload.name}": ${payload.status === 'completed' ? 'registrada como realizada' : `creada (${statusLabel})`}`,
        );
      }
      return data.id as string;
    },
    onSuccess: (_id, vars) => {
      qc.invalidateQueries({ queryKey: ['mentorship-sessions', vars.group_id] });
      qc.invalidateQueries({ queryKey: ['mentorship-project-sessions', vars.projectId] });
      qc.invalidateQueries({ queryKey: ['mentorship-hours', vars.projectId] });
      qc.invalidateQueries({ queryKey: ['mentorship-hours-log', vars.projectId] });
      qc.invalidateQueries({ queryKey: ['mentorship-session-mentors'] });
      qc.invalidateQueries({ queryKey: ['mentorship-mentor-report', vars.projectId] });
    },
  });
}

export function useDeleteMentorshipSession() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ sessionId, groupId, projectId }: { sessionId: string; groupId: string; projectId: string }) => {
      const { data: session } = await supabase
        .from('mentorship_sessions')
        .select('name, duration_minutes, status')
        .eq('id', sessionId)
        .maybeSingle();

      // Atribución vigente de la sesión que se está por revertir, para que la
      // reversa quede a nombre de las mismas personas. Se lee antes de borrar:
      // después, la FK deja session_id en null en sus asientos.
      const carriedMentorIds = session?.status === 'completed'
        ? (await fetchCurrentAttribution('session_id', [sessionId])).get(sessionId) ?? []
        : [];

      const rounding = await fetchProjectRounding(projectId);
      const { error } = await supabase.from('mentorship_sessions').delete().eq('id', sessionId);
      if (error) throw error;

      // Asiento compensatorio (igual que extra_work_deleted): la sesión se borra
      // de mentorship_sessions, pero el hito queda en la bitácora con las horas
      // en reversa para que el saldo (que ya se calcula en vivo sobre
      // mentorship_sessions, no sobre este log) y la auditoría queden consistentes.
      const { data: logRow } = await supabase.from('mentorship_hours_log').insert({
        project_id: projectId,
        group_id: groupId,
        event_type: 'session_deleted',
        hours_delta: session?.status === 'completed' ? -billedSessionHours(session.duration_minutes, rounding) : null,
        description: `Sesión eliminada: "${session?.name ?? sessionId}"`,
      }).select('id').single();
      if (logRow) await attachHoursLogMentors(logRow.id, carriedMentorIds);
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['mentorship-sessions', vars.groupId] });
      qc.invalidateQueries({ queryKey: ['mentorship-project-sessions', vars.projectId] });
      qc.invalidateQueries({ queryKey: ['mentorship-hours', vars.projectId] });
      qc.invalidateQueries({ queryKey: ['mentorship-hours-log', vars.projectId] });
      qc.invalidateQueries({ queryKey: ['mentorship-session-mentors'] });
      qc.invalidateQueries({ queryKey: ['mentorship-mentor-report', vars.projectId] });
    },
  });
}

// ────────────────────────────────────────────────────────────────
// Trabajo fuera de sesión
// ────────────────────────────────────────────────────────────────

export function useMentorshipExtraWork(projectId?: string) {
  return useQuery({
    queryKey: ['mentorship-extra-work', projectId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('mentorship_extra_work')
        .select('*')
        .eq('project_id', projectId!)
        .order('work_date', { ascending: false });
      if (error) throw error;
      const rows = (data ?? []) as unknown as MentorshipExtraWork[];
      const attribution = await fetchCurrentAttribution('extra_work_id', rows.map((r) => r.id));

      const groupIds = Array.from(new Set(rows.map((r) => r.group_id).filter(Boolean))) as string[];
      const personIds = Array.from(new Set([
        ...rows.map((r) => r.created_by),
        ...Array.from(attribution.values()).flat(),
      ].filter(Boolean))) as string[];

      const groupNames = new Map<string, string>();
      if (groupIds.length) {
        const { data: gs } = await supabase.from('mentorship_groups').select('id, name').in('id', groupIds);
        (gs ?? []).forEach((g: any) => groupNames.set(g.id, g.name));
      }
      const personNames = new Map<string, string>();
      if (personIds.length) {
        const { data: ps } = await supabase.from('profiles').select('id, full_name, email').in('id', personIds);
        (ps ?? []).forEach((p: any) => personNames.set(p.id, p.full_name || p.email || 'Mentor'));
      }

      return rows.map((r) => {
        const mentorIds = attribution.get(r.id) ?? [];
        return {
          ...r,
          group_name: r.group_id ? groupNames.get(r.group_id) ?? 'Grupo' : null,
          author_name: r.created_by ? personNames.get(r.created_by) ?? 'Mentor' : null,
          mentor_ids: mentorIds,
          mentor_names: mentorIds.map((id) => personNames.get(id) ?? 'Mentor'),
        };
      }) as MentorshipExtraWorkRow[];
    },
    enabled: !!projectId,
  });
}

export function useAddExtraWork() {
  const qc = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async (input: {
      project_id: string; group_id?: string | null; work_date: string; hours: number; description: string;
      /** Mentores que hicieron el trabajo (atribución; no multiplica horas). */
      mentorIds?: string[];
    }) => {
      const rounding = await fetchProjectRounding(input.project_id);
      const { data: work, error } = await supabase.from('mentorship_extra_work').insert({
        project_id: input.project_id,
        group_id: input.group_id || null,
        work_date: input.work_date,
        hours: input.hours,
        description: input.description,
        created_by: user?.id ?? null,
      }).select('id').single();
      if (error) throw error;
      const { data: logRow } = await supabase.from('mentorship_hours_log').insert({
        project_id: input.project_id,
        group_id: input.group_id || null,
        extra_work_id: work?.id ?? null,
        event_type: 'extra_work',
        hours_delta: billedExtraHours(input.hours, rounding),
        description: `Trabajo fuera de sesión: ${input.description}`,
      }).select('id').single();
      if (logRow) await attachHoursLogMentors(logRow.id, input.mentorIds);
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['mentorship-extra-work', vars.project_id] });
      qc.invalidateQueries({ queryKey: ['mentorship-hours', vars.project_id] });
      qc.invalidateQueries({ queryKey: ['mentorship-hours-log', vars.project_id] });
      qc.invalidateQueries({ queryKey: ['mentorship-mentor-report', vars.project_id] });
    },
  });
}

export function useUpdateExtraWork() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      id: string;
      project_id: string;
      group_id?: string | null;
      work_date: string;
      hours: number;
      description: string;
      previous_hours: number;
      /** Mentores vigentes al momento de la edición (atribución; no multiplica horas). */
      mentorIds?: string[];
    }) => {
      const rounding = await fetchProjectRounding(input.project_id);
      const { error } = await supabase
        .from('mentorship_extra_work')
        .update({
          group_id: input.group_id || null,
          work_date: input.work_date,
          hours: input.hours,
          description: input.description,
        })
        .eq('id', input.id);
      if (error) throw error;
      const { data: logRow } = await supabase.from('mentorship_hours_log').insert({
        project_id: input.project_id,
        group_id: input.group_id || null,
        extra_work_id: input.id,
        event_type: 'extra_work_updated',
        hours_delta: billedExtraHours(input.hours, rounding) - billedExtraHours(input.previous_hours, rounding),
        description: `Trabajo fuera de sesión editado: ${input.description} (${input.previous_hours} h → ${input.hours} h)`,
      }).select('id').single();
      if (logRow) await attachHoursLogMentors(logRow.id, input.mentorIds);
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['mentorship-extra-work', vars.project_id] });
      qc.invalidateQueries({ queryKey: ['mentorship-hours', vars.project_id] });
      qc.invalidateQueries({ queryKey: ['mentorship-hours-log', vars.project_id] });
      qc.invalidateQueries({ queryKey: ['mentorship-mentor-report', vars.project_id] });
    },
  });
}

export function useDeleteExtraWork() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, projectId, groupId, hours, description }: { id: string; projectId: string; groupId?: string | null; hours?: number; description?: string | null }) => {
      // Atribución vigente del trabajo que se está por revertir, para que la reversa
      // quede a nombre de las mismas personas. Se lee antes de borrar: después, la
      // FK deja extra_work_id en null en sus asientos.
      const carriedMentorIds = (await fetchCurrentAttribution('extra_work_id', [id])).get(id) ?? [];

      const rounding = await fetchProjectRounding(projectId);
      const { error } = await supabase.from('mentorship_extra_work').delete().eq('id', id);
      if (error) throw error;
      const { data: logRow } = await supabase.from('mentorship_hours_log').insert({
        project_id: projectId,
        group_id: groupId || null,
        event_type: 'extra_work_deleted',
        hours_delta: hours ? -billedExtraHours(hours, rounding) : null,
        description: `Trabajo fuera de sesión eliminado: ${description ?? ''}`.trim(),
      }).select('id').single();
      if (logRow) await attachHoursLogMentors(logRow.id, carriedMentorIds);
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['mentorship-extra-work', vars.projectId] });
      qc.invalidateQueries({ queryKey: ['mentorship-hours', vars.projectId] });
      qc.invalidateQueries({ queryKey: ['mentorship-hours-log', vars.projectId] });
      qc.invalidateQueries({ queryKey: ['mentorship-mentor-report', vars.projectId] });
    },
  });
}


// ────────────────────────────────────────────────────────────────
// Tareas
// ────────────────────────────────────────────────────────────────

export function useMentorshipTasks(groupId?: string) {
  return useQuery({
    queryKey: ['mentorship-tasks', groupId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('mentorship_tasks')
        .select('*')
        .eq('group_id', groupId!)
        .order('due_date', { ascending: true, nullsFirst: false });
      if (error) throw error;
      return (data ?? []) as unknown as MentorshipTask[];
    },
    enabled: !!groupId,
  });
}

export function useProjectTasks(projectId?: string) {
  return useQuery({
    queryKey: ['mentorship-project-tasks', projectId],
    queryFn: async () => {
      const { data: groups, error: gErr } = await supabase
        .from('mentorship_groups')
        .select('id, name')
        .eq('project_id', projectId!);
      if (gErr) throw gErr;
      const ids = (groups ?? []).map((g) => g.id);
      if (!ids.length) return [] as (MentorshipTask & { groupName: string })[];
      const { data, error } = await supabase
        .from('mentorship_tasks')
        .select('*')
        .in('group_id', ids);
      if (error) throw error;
      const nameById = new Map((groups ?? []).map((g) => [g.id, g.name]));
      return ((data ?? []) as unknown as MentorshipTask[]).map((t) => ({
        ...t,
        groupName: nameById.get(t.group_id) ?? '',
      }));
    },
    enabled: !!projectId,
  });
}

export function useSaveMentorshipTask() {
  const qc = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async (input: Partial<MentorshipTask> & { id?: string; group_id: string }) => {
      const { id, ...rest } = input as any;
      const payload = {
        group_id: rest.group_id,
        session_id: rest.session_id || null,
        title: rest.title,
        description: rest.description ?? null,
        assignee_id: rest.assignee_id || null,
        due_date: rest.due_date || null,
        status: rest.status ?? 'pending',
        evidence_url: rest.evidence_url ?? null,
        evidence_text: rest.evidence_text ?? null,
        completed_at: rest.status === 'done' ? new Date().toISOString() : null,
      };
      if (id) {
        const { error } = await supabase
          .from('mentorship_tasks')
          .update({ ...payload, updated_at: new Date().toISOString() })
          .eq('id', id);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('mentorship_tasks')
          .insert({ ...payload, created_by: user?.id ?? null });
        if (error) throw error;
      }
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['mentorship-tasks', vars.group_id] });
      qc.invalidateQueries({ queryKey: ['mentorship-project-tasks'] });
    },
  });
}

export function useDeleteMentorshipTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id }: { id: string; groupId: string }) => {
      const { error } = await supabase.from('mentorship_tasks').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: (_d, vars) => qc.invalidateQueries({ queryKey: ['mentorship-tasks', vars.groupId] }),
  });
}

export function useTaskComments(taskId?: string) {
  return useQuery({
    queryKey: ['mentorship-task-comments', taskId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('mentorship_task_comments')
        .select('*')
        .eq('task_id', taskId!)
        .order('created_at');
      if (error) throw error;
      return data ?? [];
    },
    enabled: !!taskId,
  });
}

export function useAddTaskComment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ taskId, body }: { taskId: string; body: string }) => {
      const { error } = await supabase.from('mentorship_task_comments').insert({ task_id: taskId, body });
      if (error) throw error;
    },
    onSuccess: (_d, vars) => qc.invalidateQueries({ queryKey: ['mentorship-task-comments', vars.taskId] }),
  });
}

// ────────────────────────────────────────────────────────────────
// Horas y bitácora
// ────────────────────────────────────────────────────────────────

export interface MentorshipHours {
  contracted: number;
  assigned: number;
  planned: number;
  executed: number;
  remaining: number;
  usedPct: number;
}

export function useMentorshipHours(projectId?: string, contractedHours = 0) {
  return useQuery({
    queryKey: ['mentorship-hours', projectId, contractedHours],
    queryFn: async (): Promise<MentorshipHours> => {
      const { data: groups } = await supabase
        .from('mentorship_groups')
        .select('id, estimated_hours')
        .eq('project_id', projectId!);
      const ids = (groups ?? []).map((g) => g.id);
      const rounding = await fetchProjectRounding(projectId!);
      const assigned = (groups ?? []).reduce((s, g) => s + Number(g.estimated_hours || 0), 0);

      let planned = 0;
      let executed = 0;
      if (ids.length) {
        const { data: sessions } = await supabase
          .from('mentorship_sessions')
          .select('duration_minutes, status')
          .in('group_id', ids);
        for (const s of sessions ?? []) {
          if (s.status === 'completed') executed += billedSessionHours(s.duration_minutes, rounding);
          else if (s.status === 'planned') planned += billedSessionHours(s.duration_minutes, rounding);
        }
      }

      const { data: extra } = await supabase
        .from('mentorship_extra_work')
        .select('hours')
        .eq('project_id', projectId!);
      executed += (extra ?? []).reduce((s, e) => s + billedExtraHours(e.hours, rounding), 0);

      const remaining = Math.max(0, contractedHours - executed);
      const usedPct = contractedHours > 0 ? Math.min(100, (executed / contractedHours) * 100) : 0;
      return { contracted: contractedHours, assigned, planned, executed, remaining, usedPct };
    },
    enabled: !!projectId,
  });
}

/** Atribución vigente de varias sesiones: Map<sessionId, mentorIds>. Para listarlas y precargar la edición. */
export function useSessionsCurrentMentors(sessionIds: string[]) {
  const key = [...sessionIds].sort().join(',');
  return useQuery({
    queryKey: ['mentorship-session-mentors', key],
    queryFn: () => fetchCurrentAttribution('session_id', sessionIds),
    enabled: sessionIds.length > 0,
  });
}

export function useMentorshipHoursLog(projectId?: string) {
  return useQuery({
    queryKey: ['mentorship-hours-log', projectId],
    queryFn: async (): Promise<MentorshipHoursLogEntry[]> => {
      const { data, error } = await supabase
        .from('mentorship_hours_log')
        .select('*')
        .eq('project_id', projectId!)
        .order('created_at', { ascending: false })
        .limit(200);
      if (error) throw error;
      const rows = (data ?? []) as unknown as Omit<MentorshipHoursLogEntry, 'mentors'>[];
      if (!rows.length) return [];

      const { data: links } = await supabase
        .from('mentorship_hours_log_mentors')
        .select('hours_log_id, mentor_id, is_primary')
        .in('hours_log_id', rows.map((r) => r.id));

      const mentorIds = Array.from(new Set((links ?? []).map((l) => l.mentor_id)));
      const namesById = new Map<string, string | null>();
      if (mentorIds.length) {
        const { data: profs } = await supabase.from('profiles').select('id, full_name').in('id', mentorIds);
        (profs ?? []).forEach((p: any) => namesById.set(p.id, p.full_name));
      }

      const mentorsByLog = new Map<string, MentorshipHoursLogMentor[]>();
      (links ?? []).forEach((l: any) => {
        const list = mentorsByLog.get(l.hours_log_id) ?? [];
        list.push({ mentor_id: l.mentor_id, full_name: namesById.get(l.mentor_id) ?? null, is_primary: l.is_primary });
        mentorsByLog.set(l.hours_log_id, list);
      });

      return rows.map((r) => ({ ...r, mentors: mentorsByLog.get(r.id) ?? [] }));
    },
    enabled: !!projectId,
  });
}

export interface MentorReportItem {
  kind: 'session' | 'extra_work';
  id: string;
  label: string;
  groupName: string | null;
  date: string | null;
  hours: number;
  mentorIds: string[];
  /** Tiene más de un mentor: aparece completo en el reporte de cada uno. */
  shared: boolean;
}

export interface MentorReportRow {
  mentorId: string;
  fullName: string;
  hours: number;
  sharedHours: number;
  items: MentorReportItem[];
}

export interface MentorReport {
  rows: MentorReportRow[];
  /** Registros que consumen bolsa pero no tienen ningún mentor atribuido. */
  unattributed: MentorReportItem[];
  /** Suma de cada registro una sola vez: coincide con las horas ejecutadas de la bolsa. */
  consolidatedHours: number;
}

/**
 * Reporte por mentor. Parte de los mismos registros que consumen la bolsa
 * (sesiones realizadas y trabajo fuera de sesión, con las horas que se cobran) y les
 * cruza la atribución vigente. Un registro compartido aparece completo en el
 * reporte de cada mentor, marcado; el consolidado lo cuenta una sola vez.
 */
export function useMentorshipMentorReport(projectId?: string) {
  return useQuery({
    queryKey: ['mentorship-mentor-report', projectId],
    queryFn: async (): Promise<MentorReport> => {
      const { data: groups, error: gErr } = await supabase
        .from('mentorship_groups')
        .select('id, name')
        .eq('project_id', projectId!);
      if (gErr) throw gErr;
      const groupName = new Map((groups ?? []).map((g) => [g.id, g.name]));
      const groupIds = (groups ?? []).map((g) => g.id);
      const rounding = await fetchProjectRounding(projectId!);

      const [sessionsRes, extraRes] = await Promise.all([
        groupIds.length
          ? supabase
              .from('mentorship_sessions')
              .select('id, group_id, name, scheduled_date, duration_minutes')
              .in('group_id', groupIds)
              .eq('status', 'completed')
          : Promise.resolve({ data: [], error: null }),
        supabase
          .from('mentorship_extra_work')
          .select('id, group_id, work_date, hours, description')
          .eq('project_id', projectId!),
      ]);
      if (sessionsRes.error) throw sessionsRes.error;
      if (extraRes.error) throw extraRes.error;
      const sessions = sessionsRes.data ?? [];
      const extra = extraRes.data ?? [];

      const [sessionMentors, extraMentors] = await Promise.all([
        fetchCurrentAttribution('session_id', sessions.map((s) => s.id)),
        fetchCurrentAttribution('extra_work_id', extra.map((e) => e.id)),
      ]);

      const items: MentorReportItem[] = [
        ...sessions.map((s) => ({
          kind: 'session' as const,
          id: s.id,
          label: s.name,
          groupName: groupName.get(s.group_id) ?? null,
          date: s.scheduled_date,
          hours: billedSessionHours(s.duration_minutes, rounding),
          mentorIds: sessionMentors.get(s.id) ?? [],
        })),
        ...extra.map((e) => ({
          kind: 'extra_work' as const,
          id: e.id,
          label: e.description || 'Trabajo fuera de sesión',
          groupName: e.group_id ? groupName.get(e.group_id) ?? null : null,
          date: e.work_date,
          hours: billedExtraHours(e.hours, rounding),
          mentorIds: extraMentors.get(e.id) ?? [],
        })),
      ].map((i) => ({ ...i, shared: i.mentorIds.length > 1 }));

      const mentorIds = Array.from(new Set(items.flatMap((i) => i.mentorIds)));
      const names = new Map<string, string>();
      if (mentorIds.length) {
        const { data: profs } = await supabase.from('profiles').select('id, full_name, email').in('id', mentorIds);
        (profs ?? []).forEach((p) => names.set(p.id, p.full_name || p.email || 'Mentor'));
      }

      const byMentor = new Map<string, MentorReportRow>();
      for (const item of items) {
        for (const mentorId of item.mentorIds) {
          const row = byMentor.get(mentorId) ?? { mentorId, fullName: names.get(mentorId) ?? 'Mentor', hours: 0, sharedHours: 0, items: [] };
          row.hours += item.hours;
          if (item.shared) row.sharedHours += item.hours;
          row.items.push(item);
          byMentor.set(mentorId, row);
        }
      }

      return {
        rows: Array.from(byMentor.values()).sort((a, b) => b.hours - a.hours),
        unattributed: items.filter((i) => i.mentorIds.length === 0),
        consolidatedHours: items.reduce((sum, i) => sum + i.hours, 0),
      };
    },
    enabled: !!projectId,
  });
}

// ────────────────────────────────────────────────────────────────
// Personas
// ────────────────────────────────────────────────────────────────

/**
 * Todos los mentores de la plataforma (rol mentor). Si el apoyo del grupo no
 * puede, la sesión la da el líder o cualquier otro mentor: esta es la lista
 * para atribuir horas. Sale de una función de la base porque un mentor no puede
 * leer los roles de los demás.
 */
export function usePlatformMentors() {
  return useQuery({
    queryKey: ['mentorship-platform-mentors'],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('mentorship_platform_mentors');
      if (error) throw error;
      return (data ?? []) as { id: string; full_name: string | null; email: string }[];
    },
    staleTime: 5 * 60 * 1000,
  });
}

export interface MentorshipMentorDirectory {
  leadId: string | null;
  /** Mentores de apoyo de la mentoría (rol apoyo). */
  supportIds: string[];
  /** Líder + apoyos. */
  mentoriaIds: string[];
  /** Mentores de la plataforma que no son de esta mentoría. */
  otherIds: string[];
  nameById: Map<string, string>;
}

/** Las figuras de Vivetori de una mentoría y el resto de mentores de la plataforma, con nombre. */
export function useMentorshipMentorDirectory(project: MentorshipProject | null | undefined): MentorshipMentorDirectory {
  const mentoriaIds = useMemo(() => projectMentorIds(project), [project]);
  // Sin `= []`: un arreglo nuevo en cada render rompería la memoización.
  const { data: people } = usePeopleByIds(mentoriaIds);
  const { data: platform } = usePlatformMentors();
  return useMemo(() => {
    const nameById = new Map<string, string>();
    (platform ?? []).forEach((m) => nameById.set(m.id, m.full_name || m.email));
    (people ?? []).forEach((p) => nameById.set(p.id, p.full_name || p.email));
    return {
      leadId: project?.lead_mentor_id ?? null,
      supportIds: projectSupportMentorIds(project),
      mentoriaIds,
      otherIds: (platform ?? []).map((m) => m.id).filter((id) => !mentoriaIds.includes(id)),
      nameById,
    };
  }, [project, mentoriaIds, people, platform]);
}

/** Usuarios con rol mentor o super admin — para asignar mentores (solo super admin). */
export function useMentorCandidates(enabled = true) {
  return useQuery({
    queryKey: ['mentorship-mentor-candidates'],
    queryFn: async () => {
      const { data: roleRows, error } = await supabase
        .from('user_roles')
        .select('user_id, role')
        .in('role', ['mentor', 'super_admin']);
      if (error) throw error;
      const ids = Array.from(new Set((roleRows ?? []).map((r) => r.user_id)));
      if (!ids.length) return [] as MentorshipPerson[];
      const { data: profiles } = await supabase
        .from('profiles')
        .select('id, full_name, email, organization_id, company')
        .in('id', ids)
        .order('full_name');
      return (profiles ?? []) as MentorshipPerson[];
    },
    enabled,
  });
}

/** Búsqueda de usuarios de la plataforma para vincular como participantes. */
export function useUserSearch(term: string, organizationId?: string | null, onlyOrg = true) {
  return useQuery({
    queryKey: ['mentorship-user-search', term, organizationId, onlyOrg],
    queryFn: async () => {
      let query = supabase
        .from('profiles')
        .select('id, full_name, email, organization_id, company')
        .order('full_name')
        .limit(25);
      if (onlyOrg && organizationId) query = query.eq('organization_id', organizationId);
      if (term.trim()) query = query.or(`full_name.ilike.%${term}%,email.ilike.%${term}%`);
      const { data, error } = await query;
      if (error) throw error;
      return (data ?? []) as MentorshipPerson[];
    },
    enabled: onlyOrg ? !!organizationId : term.trim().length >= 2,
  });
}

export function usePeopleByIds(ids: string[]) {
  const key = [...ids].sort().join(',');
  return useQuery({
    queryKey: ['mentorship-people', key],
    queryFn: async () => {
      if (!ids.length) return [] as MentorshipPerson[];
      const { data, error } = await supabase
        .from('profiles')
        .select('id, full_name, email, organization_id, company')
        .in('id', ids);
      if (error) throw error;
      return (data ?? []) as MentorshipPerson[];
    },
    enabled: ids.length > 0,
  });
}

export function useOrganizationsList() {
  return useQuery({
    queryKey: ['mentorship-organizations'],
    queryFn: async () => {
      const { data, error } = await supabase.from('organizations').select('id, name').order('name');
      if (error) throw error;
      return (data ?? []) as { id: string; name: string }[];
    },
  });
}
